'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { BookingStepper } from './BookingStepper';
import { BookingTypePicker } from './BookingTypePicker';
import { BookingCalendar } from './BookingCalendar';
import { SlotPicker } from './SlotPicker';
import { BookingDetailsForm } from './BookingDetailsForm';
import { BookingSummaryRail } from './BookingSummaryRail';
import { BookingPanel } from './BookingPanel';
import { BookingActionBar } from './BookingActionBar';
import type { AvailabilityResponse, BookingTypeSummary, CreateBookingResponse, SlotApi } from './types';

type Step = 1 | 2 | 3;

function detectTz(): string {
  if (typeof Intl !== 'undefined') {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    } catch {
      return 'UTC';
    }
  }
  return 'UTC';
}

function startOfMonth(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function ymdToIso(ymd: string): string {
  // Provide a noon UTC anchor so the date doesn't shift across timezones in display logic
  return `${ymd}T12:00:00Z`;
}

export function BookingWizard({ types }: { types: BookingTypeSummary[] }) {
  const router = useRouter();
  const params = useSearchParams();

  const [clientTimezone] = useState(() => detectTz());
  const [step, setStep] = useState<Step>(((Number(params.get('step')) || 1) as Step) || 1);
  const [selectedSlug, setSelectedSlug] = useState<string | null>(params.get('type'));
  const [monthCursor, setMonthCursor] = useState<Date>(startOfMonth(new Date()));
  const [selectedDate, setSelectedDate] = useState<string | null>(params.get('date'));
  const [selectedSlot, setSelectedSlot] = useState<SlotApi | null>(null);
  const [availability, setAvailability] = useState<AvailabilityResponse | null>(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [createResult, setCreateResult] = useState<CreateBookingResponse | null>(null);
  const reqIdRef = useRef(0);

  const selectedType = useMemo(
    () => types.find((t) => t.slug === selectedSlug) ?? null,
    [types, selectedSlug]
  );

  // Sync URL with state
  useEffect(() => {
    const url = new URL(window.location.href);
    url.searchParams.set('step', String(step));
    if (selectedSlug) url.searchParams.set('type', selectedSlug);
    else url.searchParams.delete('type');
    if (selectedDate) url.searchParams.set('date', selectedDate);
    else url.searchParams.delete('date');
    router.replace(url.pathname + url.search, { scroll: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, selectedSlug, selectedDate]);

  // Fetch availability when a type is chosen (or month changes)
  useEffect(() => {
    if (!selectedType) return;
    const reqId = ++reqIdRef.current;
    setLoadingSlots(true);
    const from = startOfMonth(monthCursor);
    const to = new Date(monthCursor.getFullYear(), monthCursor.getMonth() + 2, 0);
    const url = new URL('/api/availability', window.location.origin);
    url.searchParams.set('slug', selectedType.slug);
    url.searchParams.set('from', from.toISOString());
    url.searchParams.set('to', to.toISOString());
    url.searchParams.set('tz', clientTimezone);
    fetch(url.toString())
      .then((r) => r.json())
      .then((data: AvailabilityResponse) => {
        if (reqIdRef.current !== reqId) return;
        setAvailability(data);
      })
      .catch(() => {
        /* swallow */
      })
      .finally(() => {
        if (reqIdRef.current === reqId) setLoadingSlots(false);
      });
  }, [selectedType, monthCursor, clientTimezone]);

  const availableDays = useMemo(() => {
    if (!availability) return new Set<string>();
    const out = new Set<string>();
    for (const [dateKey, list] of Object.entries(availability.slots)) {
      if (list.length > 0) out.add(dateKey);
    }
    return out;
  }, [availability]);

  const slotsForDay: SlotApi[] = useMemo(() => {
    if (!availability || !selectedDate) return [];
    return availability.slots[selectedDate] ?? [];
  }, [availability, selectedDate]);

  function handleTypeSelect(slug: string) {
    setSelectedSlug(slug);
    setSelectedSlot(null);
    setAvailability(null);
  }

  function handleDateSelect(date: string) {
    setSelectedDate(date);
    setSelectedSlot(null);
  }

  function handleSlotSelect(slot: SlotApi) {
    setSelectedSlot(slot);
  }

  function handleAdvance() {
    setStep(((step + 1) as Step) <= 3 ? ((step + 1) as Step) : step);
    if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function handleBack() {
    setStep(((step - 1) as Step) >= 1 ? ((step - 1) as Step) : 1);
    if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function handleCreated(res: CreateBookingResponse) {
    setCreateResult(res);
    if (!res.requiresPayment) {
      router.push(`/book/confirmed/${res.bookingNumber}?t=${encodeURIComponent(res.manageToken)}`);
    }
  }

  return (
    <div className="flex flex-col gap-12 lg:flex-row lg:items-start lg:gap-12">
      <div className="flex-1 min-w-0">
        <div className="mb-10">
          <BookingStepper step={step} />
        </div>

        {step === 1 && (
          <section>
            <header className="flex flex-col items-start gap-3 mb-8">
              <span
                className="font-mono text-accent"
                style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.2em' }}
              >
                STEP 01 · WHAT WORKS BEST
              </span>
              <h1
                className="font-display"
                style={{
                  fontSize: 'clamp(32px, 4vw, 48px)',
                  fontWeight: 800,
                  letterSpacing: '-0.03em',
                  lineHeight: 1.05,
                }}
              >
                <span className="text-text">Pick the </span>
                <span className="gradient-text italic">conversation</span>
                <span className="text-text">.</span>
              </h1>
            </header>
            <BookingTypePicker
              types={types}
              selectedSlug={selectedSlug}
              onSelect={(slug) => {
                handleTypeSelect(slug);
              }}
            />
            <BookingActionBar>
              <div className="flex justify-end">
                <button
                  type="button"
                  disabled={!selectedSlug}
                  onClick={handleAdvance}
                  className="cta-primary inline-flex items-center gap-2 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    background: 'linear-gradient(135deg, var(--accent), var(--indigo))',
                    color: 'var(--bg-primary)',
                    fontSize: 14,
                    fontWeight: 800,
                    padding: '13px 28px',
                    borderRadius: 'var(--radius-lg)',
                    cursor: selectedSlug ? 'pointer' : 'not-allowed',
                  }}
                >
                  Continue · Pick a time →
                </button>
              </div>
            </BookingActionBar>
          </section>
        )}

        {step === 2 && selectedType && (
          <section>
            <header className="flex flex-col items-start gap-3 mb-8">
              <span
                className="font-mono text-accent"
                style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.2em' }}
              >
                STEP 02 · WHEN
              </span>
              <h1
                className="font-display"
                style={{
                  fontSize: 'clamp(32px, 4vw, 48px)',
                  fontWeight: 800,
                  letterSpacing: '-0.03em',
                  lineHeight: 1.05,
                }}
              >
                <span className="text-text">Pick a </span>
                <span className="gradient-text italic">date and time</span>
                <span className="text-text">.</span>
              </h1>
            </header>
            <BookingPanel>
              <div className="grid grid-cols-1 lg:grid-cols-[460px_minmax(0,1fr)] gap-10">
                <BookingCalendar
                  monthCursor={monthCursor}
                  setMonthCursor={setMonthCursor}
                  selectedDate={selectedDate}
                  onSelect={handleDateSelect}
                  availableDays={availableDays}
                  loading={loadingSlots}
                />
                <SlotPicker
                  slotsForDay={slotsForDay}
                  clientTimezone={clientTimezone}
                  ownerTimezone={selectedType.timezone}
                  selectedStartUtc={selectedSlot?.startUtc ?? null}
                  onSelect={handleSlotSelect}
                  selectedDateIso={selectedDate ? ymdToIso(selectedDate) : null}
                  loading={loadingSlots}
                />
              </div>
              <BookingActionBar inFlowMarginTop={40}>
              <div className="flex items-center justify-between gap-4">
                <button
                  type="button"
                  onClick={handleBack}
                  className="inline-flex items-center gap-2 font-mono"
                  style={{
                    fontSize: 12,
                    letterSpacing: '0.04em',
                    color: 'var(--text-muted)',
                    background: 'transparent',
                    padding: '10px 16px',
                    borderRadius: 'var(--radius-md)',
                  }}
                >
                  ← Back
                </button>
                <button
                  type="button"
                  disabled={!selectedSlot}
                  onClick={handleAdvance}
                  className="cta-primary inline-flex items-center gap-2 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    background: 'linear-gradient(135deg, var(--accent), var(--indigo))',
                    color: 'var(--bg-primary)',
                    fontSize: 14,
                    fontWeight: 800,
                    padding: '13px 28px',
                    borderRadius: 'var(--radius-lg)',
                    cursor: selectedSlot ? 'pointer' : 'not-allowed',
                  }}
                >
                  Continue · Your details →
                </button>
              </div>
              </BookingActionBar>
            </BookingPanel>
          </section>
        )}

        {step === 3 && selectedType && selectedSlot && (
          <section>
            <header className="flex flex-col items-start gap-3 mb-8">
              <span
                className="font-mono text-accent"
                style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.2em' }}
              >
                STEP 03 · YOU
              </span>
              <h1
                className="font-display"
                style={{
                  fontSize: 'clamp(32px, 4vw, 48px)',
                  fontWeight: 800,
                  letterSpacing: '-0.03em',
                  lineHeight: 1.05,
                }}
              >
                <span className="text-text">Tell me </span>
                <span className="gradient-text italic">about you</span>
                <span className="text-text">.</span>
              </h1>
            </header>
            <BookingPanel>
              <BookingDetailsForm
                bookingType={selectedType}
                slot={selectedSlot}
                clientTimezone={clientTimezone}
                onSuccess={handleCreated}
              />
              <div className="mt-6 flex items-center justify-between gap-4">
                <button
                  type="button"
                  onClick={handleBack}
                  className="inline-flex items-center gap-2 font-mono"
                  style={{
                    fontSize: 12,
                    letterSpacing: '0.04em',
                    color: 'var(--text-muted)',
                    background: 'transparent',
                    padding: '10px 16px',
                    borderRadius: 'var(--radius-md)',
                  }}
                >
                  ← Back
                </button>
              </div>
            </BookingPanel>
          </section>
        )}
      </div>

      <BookingSummaryRail
        step={step}
        bookingType={selectedType}
        slot={selectedSlot}
        clientTimezone={clientTimezone}
      />
    </div>
  );
}
